import { Input, NoticeBase, SelectNew, TextArea } from "@synthesis/shared-ui";
import { useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { useCreateApplicationDictionariesContext } from "../../create-application-dictionaries-context";
import { toSelectItems, type AdjacentObjectDataFormValues } from "../../create-application-page.lib";
import { AdjacentObjectDataCheckbox } from "./create-application-adjacent-object-data-form-controls";
import { CreateApplicationFormField, CreateApplicationFormRow, CreateApplicationFormSection } from "../create-application-form-layout";

export const AdjacentObjectCommonSection = () => {
  const { dictionaries } = useCreateApplicationDictionariesContext();
  const methods = useFormContext<AdjacentObjectDataFormValues>();
  const fundingSource = useWatch({ name: "fundingSource" });
  const isLicensedSubsoilDevelopment = useWatch({ name: "licensedSubsoilDevelopment" });
  const hasDesignDocumentationExpertise = useWatch({ name: "hasDesignDocumentationExpertise" });

  const { setValue } = methods;

  useEffect(() => {
    if (fundingSource !== "OTHER") {
      setValue("fundingSourceOther", "", { shouldDirty: false, shouldValidate: false });
    }
  }, [fundingSource, setValue]);

  const handleLicensedSubsoilDevelopmentChange = (checked: boolean) => {
    if (!checked) {
      methods.setValue("subsoilLicenseNumberAndDate", "", { shouldValidate: true });
      methods.setValue("subsoilLicenseValidity", "", { shouldValidate: true });
    }
  };

  const handleDesignDocumentationExpertiseChange = (checked: boolean) => {
    if (!checked) {
      methods.setValue("designDocumentationExpertiseDetails", "", { shouldValidate: true });
    }
  };

  return (
    <CreateApplicationFormSection title="Общая информация об объекте" required>
      <CreateApplicationFormField label="Название проекта / объекта примыкания" required>
        <Input name="projectName" placeholder="Железнодорожный путь необщего пользования ООО ТрансТехТерминал" />
      </CreateApplicationFormField>

      <CreateApplicationFormRow>
        <CreateApplicationFormField label="Вид планируемых работ" required>
          <SelectNew
            controlled
            controlledClear
            name="plannedWorkType"
            placeholder="Выберите из списка"
            items={toSelectItems(dictionaries.plannedWorksTypes)}
            required
            size="md"
            isSearch={false}
          />
        </CreateApplicationFormField>

        <CreateApplicationFormField label="Тип примыкания" required>
          <SelectNew
            controlled
            controlledClear
            name="connectionType"
            placeholder="Выберите из списка"
            items={toSelectItems(dictionaries.adjoiningTypes)}
            required
            size="md"
            isSearch={false}
          />
        </CreateApplicationFormField>
      </CreateApplicationFormRow>

      <CreateApplicationFormField label="Функциональное назначение пути">
        <Input name="trackPurpose" placeholder="Например: Путь для погрузки зерна на элеваторе" />
      </CreateApplicationFormField>

      <CreateApplicationFormRow>
        <CreateApplicationFormField label="Предварительный источник финансирования" required>
          <SelectNew
            controlled
            controlledClear
            name="fundingSource"
            placeholder="Выберите из списка"
            items={toSelectItems(dictionaries.trackFundings)}
            required
            size="md"
            isSearch={false}
          />
        </CreateApplicationFormField>

        <CreateApplicationFormField label="Протяжённость пути, м" optionalHint="(если известно)">
          <Input name="pathLengthMeters" placeholder="Например: 1500 или 2500.5" />
        </CreateApplicationFormField>
      </CreateApplicationFormRow>

      {fundingSource === "OTHER" && (
        <CreateApplicationFormField label="Уточните источник финансирования" className="lg:max-w-[calc(50%-0.5rem)]">
          <Input name="fundingSourceOther" placeholder="Например: Привлеченные инвестиции от частного партнера" />
        </CreateApplicationFormField>
      )}

      <CreateApplicationFormField label="Краткое описание проектных решений">
        <NoticeBase
          status="info"
          titleAppearance="plain"
          title="Укажите дополнительные детали проекта, которые не были указаны выше (например: характер груза, этапность строительства) или технические характеристики."
          className="max-w-none"
        />
        <TextArea
          name="projectDescription"
          rows={4}
          placeholder='Пример: "Проект реализуется в два этапа: сначала строится обгонный путь, затем - погрузочный".'
        />
      </CreateApplicationFormField>

      <AdjacentObjectDataCheckbox
        name="licensedSubsoilDevelopment"
        label="Проект связан с разработкой участков недр, подлежащей лицензированию"
        hint="Отметьте, если проект связан с разработкой месторождений полезных ископаемых и реализуется в рамках лицензии на пользование недрами."
        onCheckedChange={handleLicensedSubsoilDevelopmentChange}
      />

      {isLicensedSubsoilDevelopment && (
        <CreateApplicationFormRow>
          <CreateApplicationFormField label="Номер и дата лицензии на пользование недрами" required>
            <Input name="subsoilLicenseNumberAndDate" placeholder="№12345 от 12.03.2020" />
          </CreateApplicationFormField>

          <CreateApplicationFormField label="Срок действия лицензии" required>
            <Input name="subsoilLicenseValidity" placeholder="до 31.12.2030" />
          </CreateApplicationFormField>
        </CreateApplicationFormRow>
      )}

      <AdjacentObjectDataCheckbox
        name="hasDesignDocumentationExpertise"
        label="Есть заключение экспертизы проектной документации"
        hint="Отметьте, если проектная документация прошла экспертизу и получено положительное заключение"
        onCheckedChange={handleDesignDocumentationExpertiseChange}
      />

      {hasDesignDocumentationExpertise && (
        <CreateApplicationFormField label="Номер и дата заключения экспертизы" required>
          <Input name="designDocumentationExpertiseDetails" placeholder="№12345 от 12.03.2020" />
        </CreateApplicationFormField>
      )}
    </CreateApplicationFormSection>
  );
};
